import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { userColumns } from "../../datatablesource";
import { Link, useLocation } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, TextField, Box, Typography as MuiTypography } from "@mui/material";
import { useState, useEffect } from "react";
import ViewModal from "../ViewModal";
import { Typography } from "@mui/material";
import { formatCurrency } from '../../utils/currencyFormatter';
import { toTitleCase } from '../../utils/stringUtils';
import { paymentWorkflowService } from '../../firebase/paymentWorkflowService';

const Datatable = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { 
    users, 
    apartments, 
    payments, 
    maintenance, 
    loading, 
    error,
    deleteUser, 
    deleteApartment, 
    deletePayment, 
    deleteMaintenance,
    refreshData
  } = useData();

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [componentError, setComponentError] = useState(null);
  const [paymentModes, setPaymentModes] = useState({});
  const [showDropdown, setShowDropdown] = useState({});
  const [modalState, setModalState] = useState({ open: false, type: '', paymentId: null });
  const [cashForm, setCashForm] = useState({ amount: '', date: '', reference: '', remarks: '' });
  const [ewalletForm, setEwalletForm] = useState({ amount: '', date: '', reference: '', file: null });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, type: '', data: null });

  // Reset component error when location changes
  useEffect(() => {
    setComponentError(null);
  }, [location.pathname]);

  // Validate that required functions are available
  useEffect(() => {
    if (!deleteUser || !deleteApartment || !deletePayment || !deleteMaintenance) {
      console.error('=== MISSING DELETE FUNCTIONS ===');
      console.error('deleteUser:', !!deleteUser);
      console.error('deleteApartment:', !!deleteApartment);
      console.error('deletePayment:', !!deletePayment);
      console.error('deleteMaintenance:', !!deleteMaintenance);
      setComponentError('Delete functions are not available. Please refresh the page.');
    }
  }, [deleteUser, deleteApartment, deletePayment, deleteMaintenance]);

  // Define getTitle function outside try-catch so it can be used in error UI
  const getTitle = () => {
    switch (location.pathname) {
      case "/users":
        return "Users";
      case "/apartments":
        return "Apartments";
      case "/payments":
        return user?.role === "tenant" ? "My Payments" : "Payments";
      case "/maintenance":
        return user?.role === "tenant" ? "My Maintenance Requests" : "Maintenance Requests";
      case "/notifications":
        return "Notifications";
      case "/logs":
        return "System Logs";
      default:
        return "Data";
    }
  };

  // If there's a component error, show error UI
  if (componentError) {
    return (
      <div className="datatable">
        <div className="datatableTitle">
          {getTitle()}
        </div>
        <Alert severity="error" className="error-alert">
          <Typography variant="h6">Something went wrong</Typography>
          <Typography variant="body2">{componentError}</Typography>
          <Button 
            variant="contained" 
            onClick={() => setComponentError(null)}
            style={{ marginTop: '10px' }}
          >
            Try Again
          </Button>
        </Alert>
      </div>
    );
  }

  // Wrap all the main logic in try-catch to prevent white screens
  try {

  // Static data for notifications and logs (since we removed them from datatablesource)
  const notificationRows = [
    {
      id: 1,
      title: "Maintenance Update",
      message: "Your maintenance request has been scheduled",
      date: "2024-03-15",
      status: "Unread",
    },
    {
      id: 2,
      title: "Payment Reminder",
      message: "Rent payment is due in 3 days",
      date: "2024-03-14",
      status: "Read",
    },
    {
      id: 3,
      title: "System Update",
      message: "New features have been added to the dashboard",
      date: "2024-03-13",
      status: "Unread",
    },
  ];

  const logRows = [
    {
      id: 1,
      action: "Login",
      user: "admin@apartment.com",
      date: "2024-03-15 10:30",
      details: "Successful login from IP 192.168.1.1",
    },
    {
      id: 2,
      action: "Update",
      user: "admin@apartment.com",
      date: "2024-03-15 11:45",
      details: "Updated tenant information for Apt 101",
    },
    {
      id: 3,
      action: "Delete",
      user: "admin@apartment.com",
      date: "2024-03-15 14:20",
      details: "Deleted maintenance request #123",
    },
  ];

  // Check if user can perform specific actions based on role and current page
  const canAdd = () => {
    if (location.pathname === "/payments") {
      return user?.role === "admin" || user?.role === "tenant";
    }
    if (location.pathname === "/maintenance") {
      return user?.role === "admin" || user?.role === "tenant";
    }
    return user?.role === "admin";
  };

  const canDelete = (item) => {
    if (location.pathname === "/payments") {
      // Only admins can delete payments
      return user?.role === "admin";
    }
    if (location.pathname === "/maintenance") {
      // Tenants can delete their own maintenance requests, admins can delete any
      if (user?.role === "admin") {
        return true;
      }
      if (user?.role === "tenant") {
        const userId = user.id || user.uid;
        const requestTenantId = item?.tenantId || item?.uid;
        return requestTenantId === userId;
      }
      return false;
    }
    // For other pages, only admins can delete
    return user?.role === "admin";
  };

  const canView = (row) => {
    if (!user) return false;
    if (location.pathname === "/payments" && user.role === "tenant") {
      // Allow tenants to view their own payments if status is Processing or Paid
      const userId = user.id || user.uid;
      const paymentTenantId = row?.tenantId || row?.uid;
      const status = (row?.status || '').toLowerCase();
      return paymentTenantId === userId && (status === 'processing' || status === 'paid');
    }
    // All other cases: allow view for authenticated users
    return !!user;
  };

  const getData = () => {
    switch (location.pathname) {
      case "/users":
        return users;
      case "/apartments":
        return apartments;
      case "/payments":
        // For tenants, only show their own payments
        if (user?.role === "tenant") {
          // Use both uid and id for compatibility
          const userId = user.id || user.uid;
          return payments.filter(payment => {
            const paymentTenantId = payment.tenantId || payment.uid;
            return paymentTenantId === userId;
          });
        }
        return payments;
      case "/maintenance":
        return maintenance;
      case "/notifications":
        return notificationRows;
      case "/logs":
        return logRows;
      default:
        return users;
    }
  };

  const getColumns = () => {
    switch (location.pathname) {
      case "/users":
        return userColumns;
      case "/apartments":
        return [
          { field: "id", headerName: "ID", width: 70 },
          { field: "apartmentNumber", headerName: "Apartment", width: 150 },
          { field: "building", headerName: "Building", width: 150 },
          { field: "floor", headerName: "Floor", width: 100 },
          { field: "bedrooms", headerName: "Bedrooms", width: 120 },
          { field: "bathrooms", headerName: "Bathrooms", width: 120 },
          { field: "squareFeet", headerName: "Sq Ft", width: 100 },
          { 
            field: "monthlyRent", 
            headerName: "Rent", 
            width: 120,
            renderCell: (params) => {
              return params.value ? formatCurrency(params.value) : 'N/A';
            }
          },
          { field: "status", headerName: "Status", width: 150 },
        ];
      case "/payments":
        return [
          { field: "id", headerName: "ID", width: 70 },
          { field: "tenantName", headerName: "Tenant", width: 200 },
          { 
            field: "amount", 
            headerName: "Amount", 
            width: 130,
            renderCell: (params) => {
              return params.value ? formatCurrency(params.value) : 'N/A';
            }
          },
          {
            field: "paymentDate",
            headerName: "Date",
            width: 200,
            renderCell: (params) => {
              const value = params.value;
              if (!value) return "N/A";
              if (typeof value === "object" && value.seconds) {
                // Firestore Timestamp
                return new Date(value.seconds * 1000).toLocaleDateString();
              }
              // If it's a string or Date
              try {
                return new Date(value).toLocaleDateString();
              } catch {
                return value;
              }
            },
          },
          { field: "status", headerName: "Status", width: 130, renderCell: (params) => toTitleCase(params.value) },
        ];
      case "/maintenance":
        return [
          { field: "id", headerName: "ID", width: 70 },
          { field: "apartmentNumber", headerName: "Apartment", width: 200 },
          { field: "issue", headerName: "Issue", width: 200 },
          {
            field: "requestDate",
            headerName: "Date",
            width: 200,
            renderCell: (params) => {
              const value = params.value;
              if (!value) return "N/A";
              if (typeof value === "object" && value.seconds) {
                // Firestore Timestamp
                return new Date(value.seconds * 1000).toLocaleDateString();
              }
              // If it's a string or Date
              try {
                return new Date(value).toLocaleDateString();
              } catch {
                return value;
              }
            },
          },
          { field: "status", headerName: "Status", width: 130, renderCell: (params) => toTitleCase(params.value) },
        ];
      case "/notifications":
        return [
          { field: "id", headerName: "ID", width: 70 },
          { field: "title", headerName: "Title", width: 200 },
          { field: "message", headerName: "Message", width: 300 },
          { field: "date", headerName: "Date", width: 200 },
          { field: "status", headerName: "Status", width: 130 },
        ];
      case "/logs":
        return [
          { field: "id", headerName: "ID", width: 70 },
          { field: "action", headerName: "Action", width: 200 },
          { field: "user", headerName: "User", width: 200 },
          { field: "date", headerName: "Date", width: 200 },
          { field: "details", headerName: "Details", width: 300 },
        ];
      default:
        return userColumns;
    }
  };

  const shouldShowAddNew = () => {
    // Don't show "Add New" for logs, notifications, and other read-only sections
    if (["/logs", "/notifications"].includes(location.pathname)) {
      return false;
    }
    // Hide Add New for tenants on payments page
    if (location.pathname === "/payments" && user?.role === "tenant") {
      return false;
    }
    // Check role-based permissions
    return canAdd();
  };

  const handleDelete = async (id) => {
    console.log('Delete requested for ID:', id);
    console.log('Current pathname:', location.pathname);
    
    // Validate that we have a valid ID
    if (!id) {
      setSnackbar({ 
        open: true, 
        message: 'Unable to delete item: Invalid ID', 
        severity: 'error' 
      });
      return;
    }
    
    // Validate that data arrays are available
    if (!Array.isArray(users) || !Array.isArray(apartments) || !Array.isArray(payments) || !Array.isArray(maintenance)) {
      console.error('Data arrays are not properly initialized');
      setSnackbar({ 
        open: true, 
        message: 'Unable to delete item: Data not loaded', 
        severity: 'error' 
      });
      return;
    }
    
    // Find the item to delete for confirmation dialog
    let item;
    switch (location.pathname) {
      case "/users":
        item = users.find(user => user.id === id);
        break;
      case "/apartments":
        item = apartments.find(apt => apt.id === id);
        break;
      case "/payments":
        item = payments.find(payment => payment.id === id);
        break;
      case "/maintenance":
        item = maintenance.find(maint => maint.id === id);
        break;
      default:
        break;
    }
    
    // Check if item was found
    if (!item) {
      setSnackbar({ 
        open: true, 
        message: 'Unable to delete item: Item not found', 
        severity: 'error' 
      });
      return;
    }
    
    setItemToDelete({ id, item });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) {
      console.error('No item to delete');
      return;
    }
    
    setDeleteLoading(true);
    try {
      console.log('=== DELETE DEBUG ===');
      console.log('Confirming delete for:', itemToDelete);
      console.log('Current pathname:', location.pathname);
      console.log('Item to delete:', JSON.stringify(itemToDelete, null, 2));
      
      let result;
      let deleteFunction;
      
      switch (location.pathname) {
        case "/users":
          console.log('Deleting user with ID:', itemToDelete.id);
          deleteFunction = deleteUser;
          break;
        case "/apartments":
          console.log('Deleting apartment with ID:', itemToDelete.id);
          deleteFunction = deleteApartment;
          break;
        case "/payments":
          console.log('Deleting payment with ID:', itemToDelete.id);
          deleteFunction = deletePayment;
          break;
        case "/maintenance":
          console.log('Deleting maintenance with ID:', itemToDelete.id);
          deleteFunction = deleteMaintenance;
          break;
        default:
          console.error('Unknown pathname for deletion:', location.pathname);
          throw new Error(`Unknown pathname for deletion: ${location.pathname}`);
      }
      
      // Validate delete function exists
      if (!deleteFunction) {
        throw new Error('Delete function not found');
      }
      
      // Validate delete function is callable
      if (typeof deleteFunction !== 'function') {
        throw new Error('Delete function is not callable');
      }
      
      // Call the delete function with error handling
      console.log('Calling delete function...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Delete operation timed out')), 30000); // 30 seconds
      });
      
      const deletePromise = deleteFunction(itemToDelete.id);
      
      result = await Promise.race([deletePromise, timeoutPromise]);
      console.log('Delete result:', result);
      
      if (result && result.success) {
        console.log('✅ Delete operation successful');
        setSnackbar({ 
          open: true, 
          message: 'Item deleted successfully!', 
          severity: 'success' 
        });
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } else {
        const errorMessage = result?.error?.message || result?.error || 'Failed to delete item';
        console.error('❌ Delete failed:', errorMessage);
        console.error('Full result:', result);
        setSnackbar({ 
          open: true, 
          message: `Delete failed: ${errorMessage}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('=== DELETE ERROR ===');
      console.error('Delete error:', error);
      console.error('Error stack:', error.stack);
      console.error('Error message:', error.message);
      console.error('Error name:', error.name);
      
      // Provide more specific error messages
      let errorMessage = 'An error occurred while deleting the item';
      if (error.message.includes('permission')) {
        errorMessage = 'Permission denied. You may not have the right to delete this item.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('not-found')) {
        errorMessage = 'Item not found. It may have been already deleted.';
      } else if (error.message.includes('unavailable')) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      console.log('=== DELETE COMPLETE ===');
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleView = (row) => {
    console.log('=== VIEW DEBUG ===');
    console.log('View requested for row:', row);
    console.log('Current pathname:', location.pathname);
    console.log('Row data structure:', JSON.stringify(row, null, 2));
    console.log('Row keys:', Object.keys(row || {}));
    console.log('Row ID:', row?.id);
    console.log('Row UID:', row?.uid);
    
    // Validate that we have the necessary data
    if (!row) {
      console.error('❌ Row is null or undefined');
      setSnackbar({ 
        open: true, 
        message: 'Unable to view item: No data provided', 
        severity: 'error' 
      });
      return;
    }
    
    if (!row.id && !row.uid) {
      console.error('❌ Row has no ID or UID');
      setSnackbar({ 
        open: true, 
        message: 'Unable to view item: Invalid data structure', 
        severity: 'error' 
      });
      return;
    }
    
    console.log('✅ Data validation passed');
    console.log('Opening view modal for item:', row);
    console.log('Item type:', location.pathname.slice(1));
    
    setSelectedItem(row);
    setSelectedType(location.pathname.slice(1)); // Remove leading slash
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedItem(null);
    setSelectedType(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCashSubmit = async () => {
    // Validate required fields
    if (!cashForm.amount || !cashForm.date || !cashForm.remarks) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    // Show confirmation dialog
    setConfirmationDialog({
      open: true,
      type: 'cash',
      data: {
        paymentId: modalState.paymentId,
        ...cashForm
      }
    });
  };

  const handleEwalletSubmit = async () => {
    // Validate required fields
    if (!ewalletForm.amount || !ewalletForm.date || !ewalletForm.reference || !ewalletForm.file) {
      setSnackbar({ open: true, message: 'Please fill in all required fields and upload a receipt', severity: 'error' });
      return;
    }

    // Show confirmation dialog
    setConfirmationDialog({
      open: true,
      type: 'ewallet',
      data: {
        paymentId: modalState.paymentId,
        ...ewalletForm
      }
    });
  };

  const handlePaymentConfirmation = async () => {
    setPaymentProcessing(true);
    setConfirmationDialog({ open: false, type: '', data: null });

    try {
      let result;
      
      if (confirmationDialog.type === 'cash') {
        result = await paymentWorkflowService.processCashPayment(
          confirmationDialog.data.paymentId,
          confirmationDialog.data,
          user
        );
      } else if (confirmationDialog.type === 'ewallet') {
        result = await paymentWorkflowService.processEwalletPayment(
          confirmationDialog.data.paymentId,
          confirmationDialog.data,
          user
        );
      }

      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: result.message, 
          severity: 'success' 
        });
        
        // Close modal and reset forms
        setModalState({ open: false, type: '', paymentId: null });
        setCashForm({ amount: '', date: '', reference: '', remarks: '' });
        setEwalletForm({ amount: '', date: '', reference: '', file: null });
        
        // Refresh data to show updated status
        await refreshData();
      } else {
        setSnackbar({ 
          open: true, 
          message: `Payment submission failed: ${result.error}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setSnackbar({ 
        open: true, 
        message: 'An unexpected error occurred. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCancelConfirmation = () => {
    setConfirmationDialog({ open: false, type: '', data: null });
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 250,
      renderCell: (params) => {
        if (location.pathname === "/payments" && user?.role === "tenant") {
          const status = typeof params.row.status === 'string' ? params.row.status.toLowerCase() : '';
          if (status === 'unpaid' || status === 'pending') {
            const paymentId = params.row.id;
            const selectedMode = paymentModes[paymentId] || "Cash";
            const isDropdownVisible = showDropdown[paymentId];
            return (
              <div className="cellAction" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!isDropdownVisible ? (
                  <div
                    className="payButton"
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600 }}
                    onClick={() => setShowDropdown({ ...showDropdown, [paymentId]: true })}
                  >
                    Pay
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedMode}
                      onChange={e => setPaymentModes({ ...paymentModes, [paymentId]: e.target.value })}
                      style={{ padding: '4px', borderRadius: '4px' }}
                    >
                      <option value="Cash">Cash</option>
                      <option value="E-Wallet">E-Wallet (GCash/Maya)</option>
                      <option value="Card">Card</option>
                    </select>
                    <div
                      className="payButton"
                      style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600 }}
                      onClick={() => {
                        if (selectedMode === 'Cash') {
                          setModalState({ open: true, type: 'cash', paymentId });
                        } else if (selectedMode === 'E-Wallet') {
                          setModalState({ open: true, type: 'ewallet', paymentId });
                        } else {
                          alert(`Pay for payment ID: ${paymentId} using Card (simulated)`);
                        }
                        setShowDropdown({ ...showDropdown, [paymentId]: false });
                      }}
                    >
                      Confirm
                    </div>
                  </>
                )}
              </div>
            );
          } else if (status === 'processing' || status === 'paid') {
            return (
              <div className="cellAction">
                {canView(params.row) && (
                  <div 
                    className="viewButton"
                    onClick={() => handleView(params.row)}
                    style={{ cursor: 'pointer' }}
                  >
                    View
                  </div>
                )}
              </div>
            );
          } else {
            return null;
          }
        }
        // Default actions
        return (
          <div className="cellAction">
            {canView(params.row) && (
              <div 
                className="viewButton"
                onClick={() => handleView(params.row)}
                style={{ cursor: 'pointer' }}
              >
                View
              </div>
            )}
            {canDelete(params.row) && (
              <div
                className="deleteButton"
                onClick={() => handleDelete(params.row.id)}
              >
                Delete
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="datatable">
        <div className="datatableTitle">
          {getTitle()}
          {shouldShowAddNew() && (
            <Link to={`${location.pathname}/new`} className="link">
              Add New
            </Link>
          )}
        </div>
        <div className="loading-container">
          <CircularProgress />
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="datatable">
        <div className="datatableTitle">
          {getTitle()}
          {shouldShowAddNew() && (
            <Link to={`${location.pathname}/new`} className="link">
              Add New
            </Link>
          )}
        </div>
        <Alert severity="error" className="error-alert">
          Error loading data: {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="datatable">
      <div className="datatableTitle">
        {getTitle()}
        {shouldShowAddNew() && (
          <Link to={`${location.pathname}/new`} className="link">
            Add New
          </Link>
        )}
      </div>
      <DataGrid
        className="datagrid"
        rows={getData()}
        columns={getColumns().concat(actionColumn)}
        pageSize={9}
        rowsPerPageOptions={[9]}
        loading={loading}
      />
      
      <ViewModal
        open={viewModalOpen}
        onClose={handleCloseModal}
        data={selectedItem}
        type={selectedType}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this item?</p>
          {itemToDelete?.item && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Item to delete:</strong>
              {location.pathname === "/users" && (
                <div>
                  <p><strong>Name:</strong> {itemToDelete.item.fullName || itemToDelete.item.username || 'N/A'}</p>
                  <p><strong>Email:</strong> {itemToDelete.item.email || 'N/A'}</p>
                  <p><strong>Role:</strong> {itemToDelete.item.role || 'N/A'}</p>
                </div>
              )}
              {location.pathname === "/apartments" && (
                <div>
                  <p><strong>Apartment:</strong> {itemToDelete.item.apartmentNumber || 'N/A'}</p>
                  <p><strong>Building:</strong> {itemToDelete.item.building || 'N/A'}</p>
                </div>
              )}
              {location.pathname === "/payments" && (
                <div>
                  <p><strong>Tenant:</strong> {itemToDelete.item.tenantName || itemToDelete.item.tenant || 'N/A'}</p>
                  <p><strong>Amount:</strong> {itemToDelete.item.amount != null ? formatCurrency(itemToDelete.item.amount) : 'N/A'}</p>
                  <p><strong>Date:</strong> {itemToDelete.item.paymentDate ? (typeof itemToDelete.item.paymentDate === 'object' && itemToDelete.item.paymentDate.seconds ? new Date(itemToDelete.item.paymentDate.seconds * 1000).toLocaleDateString() : itemToDelete.item.paymentDate) : 'N/A'}</p>
                </div>
              )}
              {location.pathname === "/maintenance" && (
                <div>
                  <p><strong>Apartment:</strong> {itemToDelete.item.apartmentNumber || 'N/A'}</p>
                  <p><strong>Issue:</strong> {itemToDelete.item.issue || 'N/A'}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            autoFocus
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for Cash Payment */}
      <Dialog open={modalState.open && modalState.type === 'cash'} onClose={() => setModalState({ open: false, type: '', paymentId: null })}>
        <DialogTitle>Cash Payment Details</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 300 }}>
          <TextField label="Amount Paid" type="number" value={cashForm.amount} onChange={e => setCashForm({ ...cashForm, amount: e.target.value })} fullWidth margin="dense" />
          <TextField label="Date Paid" type="date" value={cashForm.date} onChange={e => setCashForm({ ...cashForm, date: e.target.value })} fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
          <TextField label="Reference Number (optional)" value={cashForm.reference} onChange={e => setCashForm({ ...cashForm, reference: e.target.value })} fullWidth margin="dense" />
          <TextField label="Purpose" value={cashForm.remarks} onChange={e => setCashForm({ ...cashForm, remarks: e.target.value })} fullWidth margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalState({ open: false, type: '', paymentId: null })}>Cancel</Button>
          <Button onClick={handleCashSubmit} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
      {/* Modal for E-Wallet Payment */}
      <Dialog open={modalState.open && modalState.type === 'ewallet'} onClose={() => setModalState({ open: false, type: '', paymentId: null })}>
        <DialogTitle>E-Wallet Payment (GCash/Maya)</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 300 }}>
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <img src="/ewallet-qr.jpg" alt="QR Code" style={{ width: 150, height: 150, objectFit: 'contain' }} />
            <div style={{ fontSize: 12, color: '#888' }}>Scan this QR code to pay</div>
          </div>
          <TextField label="Amount Sent" type="number" value={ewalletForm.amount} onChange={e => setEwalletForm({ ...ewalletForm, amount: e.target.value })} fullWidth margin="dense" />
          <TextField label="Date & Time" type="datetime-local" value={ewalletForm.date} onChange={e => setEwalletForm({ ...ewalletForm, date: e.target.value })} fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
          <TextField label="Reference Number" value={ewalletForm.reference} onChange={e => setEwalletForm({ ...ewalletForm, reference: e.target.value })} fullWidth margin="dense" />
          <TextField label="Purpose (optional)" value={ewalletForm.remarks} onChange={e => setEwalletForm({ ...ewalletForm, remarks: e.target.value })} fullWidth margin="dense" />
          <Button variant="outlined" component="label" style={{ marginTop: 8 }}>
            Upload Receipt Screenshot
            <input type="file" accept="image/*" hidden onChange={e => setEwalletForm({ ...ewalletForm, file: e.target.files[0] })} />
          </Button>
          {ewalletForm.file && <div style={{ fontSize: 12, color: '#1976d2' }}>{ewalletForm.file.name}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalState({ open: false, type: '', paymentId: null })}>Cancel</Button>
          <Button onClick={handleEwalletSubmit} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog 
        open={confirmationDialog.open} 
        onClose={handleCancelConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MuiTypography variant="h6">
              Confirm {confirmationDialog.type === 'cash' ? 'Cash' : 'E-Wallet'} Payment
            </MuiTypography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <MuiTypography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to submit this {confirmationDialog.type === 'cash' ? 'cash' : 'e-wallet'} payment?
          </MuiTypography>
          
          {confirmationDialog.data && (
            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <MuiTypography variant="subtitle2" sx={{ mb: 1 }}>
                Payment Details:
              </MuiTypography>
              <MuiTypography variant="body2">
                Amount: ₱{confirmationDialog.data.amount}
              </MuiTypography>
              <MuiTypography variant="body2">
                Date: {confirmationDialog.data.date}
              </MuiTypography>
              {confirmationDialog.data.reference && (
                <MuiTypography variant="body2">
                  Reference: {confirmationDialog.data.reference}
                </MuiTypography>
              )}
              {confirmationDialog.data.remarks && (
                <MuiTypography variant="body2">
                  Purpose: {confirmationDialog.data.remarks}
                </MuiTypography>
              )}
              {confirmationDialog.type === 'ewallet' && confirmationDialog.data.file && (
                <MuiTypography variant="body2">
                  Receipt: {confirmationDialog.data.file.name}
                </MuiTypography>
              )}
            </Box>
          )}
          
          <MuiTypography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            This will change the payment status to "Processing" and notify administrators.
          </MuiTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmation} disabled={paymentProcessing}>
            Cancel
          </Button>
          <Button 
            onClick={handlePaymentConfirmation} 
            variant="contained" 
            disabled={paymentProcessing}
            startIcon={paymentProcessing ? <CircularProgress size={16} /> : null}
          >
            {paymentProcessing ? 'Processing...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
  } catch (error) {
    console.error('Datatable component error:', error);
    return (
      <div className="datatable">
        <div className="datatableTitle">Error</div>
        <Alert severity="error">
          <Typography variant="h6">Something went wrong</Typography>
          <Typography variant="body2">{error.message || 'An unexpected error occurred.'}</Typography>
        </Alert>
      </div>
    );
  }
};

export default Datatable;
